export type AgentCountry = 'US' | 'IN';

export type AgentRule = {
  id: string;
  title: string;
  value?: string;
  body: string;
  badge: string;
  tone: 'goal' | 'auto' | 'approval';
};

export type PerformanceDay = {
  day: string;
  spend: string;
  sales: string;
  clicks: string;
  orders: string;
  acos: string;
  note?: string;
};

export type PerformanceHistory = {
  window: string;
  entity: string;
  rowsLabel: string;
  spend: string;
  sales: string;
  clicks: string;
  orders: string;
  acos: string;
  rows: PerformanceDay[];
};

export type AgentOpportunity = {
  id: string;
  title: string;
  badge: 'auto' | 'approval';
  badgeLabel: string;
  meta: string[];
  from?: string;
  to?: string;
  delta?: string;
  wasteAmount?: string;
  wasteDetail?: string;
  reasonLabel: string;
  reason: string;
  confidence: string;
  risk: string;
  riskTone: 'low' | 'medium';
  explanation?: string;
  approveLabel: string;
  dismissLabel?: string;
  history: PerformanceHistory;
};

export type AgentTurn =
  | { id: string; role: 'seed'; kind: 'greeting' | 'user-goal' | 'rules' | 'discovery' }
  | { id: string; role: 'user'; text: string; time: string }
  | { id: string; role: 'agent'; text: string; time: string };

export type AgentDemo = {
  country: AgentCountry;
  account: {
    name: string;
    marketplace: string;
    activeCampaigns: number;
    targetAcos: string;
    baselineAcos: string;
    dailyPace: string;
    streamAgo: string;
  };
  user: { name: string; initials: string };
  userGoal: string;
  agentReply: string;
  quickReplies: ReadonlyArray<{ title: string; body: string; prompt: string }>;
  promptChips: ReadonlyArray<{ label: string; prompt: string }>;
  rules: AgentRule[];
  pulse: {
    spend: string;
    spendDelta: string;
    sales: string;
    salesDelta: string;
    acos: string;
    acosNote: string;
    roas: string;
    roasNote: string;
    trend: string;
    forecast: string;
  };
  opportunities: AgentOpportunity[];
  guardrails: ReadonlyArray<{ id: string; title: string; detail: string; value: string }>;
};

const USER = { name: 'Sarah Chen', initials: 'SC' } as const;

const USER_GOAL =
  'Keep my ACOS below 22%. Increase budgets for profitable campaigns, reduce waste, and ask me before making risky changes.';

const AGENT_REPLY =
  'This is a demo. That instruction stays on this page and is not sent to Amazon.';

const QUICK_REPLIES = [
  {
    title: 'Reach target ACOS',
    body: 'Set an account target and calibrate aggressive bid downscaling vs top-of-search defense.',
    prompt: USER_GOAL,
  },
  {
    title: 'Automate bids & budgets',
    body: 'Define threshold limits for agentic autonomous budget shifting and hourly dayparting.',
    prompt: 'Automate my profitable bid and budget adjustments while safeguarding inventory.',
  },
  {
    title: 'Reduce wasted ad spend',
    body: 'Identify bleeding non-converting queries across Auto and Broad matching campaigns.',
    prompt: 'Harvest zero-converting search terms and add negative exact keywords.',
  },
] as const;

const RULES: AgentRule[] = [
  {
    id: 'acos',
    title: 'Target ACOS Floor',
    value: '22.0%',
    body: 'Optimize downward toward target while safeguarding top-of-search sales volume on key ASINs. Current account baseline: 24.2%.',
    badge: 'Primary Goal',
    tone: 'goal',
  },
  {
    id: 'scale',
    title: 'Profitable Campaigns Scaling',
    value: '+10% / action',
    body: 'Automatically scale budgets up to +10%/action when 7-day ROAS > 4.5x and inventory depth exceeds 30 days of supply.',
    badge: 'Automatic',
    tone: 'auto',
  },
  {
    id: 'waste',
    title: 'Bleeding Search Term Reduction',
    value: '-12% bid step',
    body: 'Automatically lower bids by up to -12% on search terms with spend > 1.5x target CPA and zero conversions over 14 days.',
    badge: 'Automatic',
    tone: 'auto',
  },
  {
    id: 'risk',
    title: 'Risky Changes Safety Protocol',
    body: 'Require explicit approval for any budget adjustment > 15%, keyword pauses, new campaign creations, or brand defense changes.',
    badge: 'Approval Required',
    tone: 'approval',
  },
];

const PULSE_RATIOS = {
  spendDelta: '-3.2% vs prev wk',
  salesDelta: '+8.4% vs prev wk',
  acos: '24.0%',
  acosNote: 'Closing gap (-2.5%)',
  roas: '4.17x',
  roasNote: 'Healthy catalog return',
  trend: '26.5% → 24.0%',
  forecast: 'Predicted target crossover in 4.2 days',
} as const;

function guardrails(maxDailyBudget: string) {
  return [
    {
      id: 'bid-step',
      title: 'Max Bid Step / Action',
      detail: 'Caps sudden single adjustments',
      value: '±15%',
    },
    {
      id: 'budget-scale',
      title: 'Max Daily Budget Scale',
      detail: 'Across any single campaign',
      value: maxDailyBudget,
    },
    {
      id: 'inventory',
      title: 'Minimum Inventory Buffer',
      detail: 'Suppress budget scale if out-of-stock risk',
      value: '> 25 Days',
    },
  ] as const;
}

const US_DEMO: AgentDemo = {
  country: 'US',
  account: {
    name: 'Anker Direct US',
    marketplace: 'Amazon.com',
    activeCampaigns: 42,
    targetAcos: '22.0%',
    baselineAcos: '24.2%',
    dailyPace: '$416/day',
    streamAgo: '2m ago',
  },
  user: USER,
  userGoal: USER_GOAL,
  agentReply: AGENT_REPLY,
  quickReplies: QUICK_REPLIES,
  promptChips: [
    {
      label: 'Show what changed today',
      prompt: 'Show what changed today across all 42 campaigns',
    },
    {
      label: 'Pause brand campaign changes',
      prompt: 'Pause brand defense campaign bid drops immediately',
    },
    {
      label: 'Why did ACOS increase yesterday?',
      prompt: 'Why did ACOS increase yesterday on the Hydro catalog?',
    },
    {
      label: 'Set max daily budget increase to $50',
      prompt: 'Set max daily budget increase to $50 without asking me',
    },
  ],
  rules: RULES,
  pulse: {
    spend: '$12,480',
    sales: '$52,000',
    ...PULSE_RATIOS,
  },
  opportunities: [
    {
      id: 'bid-1',
      title: 'Reduce bid on “stainless steel water bottle 32oz”',
      badge: 'auto',
      badgeLabel: 'Eligible for Auto-Exec',
      meta: ['Kitchen-Hydro', 'Exact Match', 'ASIN: B08NVZJ8H2'],
      from: '$1.25',
      to: '$1.08',
      delta: '-13.6%',
      reasonLabel: 'Detection',
      reason:
        'ACOS currently at 28.4% with $412 spend and 11 orders (14-day window). Bid step down stabilizes conversion efficiency without dropping page rank.',
      confidence: '94%',
      risk: 'Low Risk',
      riskTone: 'low',
      explanation:
        'Amazon Marketing Stream hourly data indicates that between 1:00 PM and 5:00 PM EST, conversion drop rate is 22% higher on this specific keyword. Down-stepping the bid to $1.08 keeps top-of-search share at 68% while eliminating $78/week in overpaid clicks.',
      history: {
        window: 'Last 14 days',
        entity: 'Exact keyword',
        rowsLabel: 'Most recent 7 days',
        spend: '$412',
        sales: '$1,451',
        clicks: '330',
        orders: '11',
        acos: '28.4%',
        rows: [
          { day: 'Oct 15', spend: '$32', sales: '$118', clicks: '26', orders: '1', acos: '27.1%' },
          { day: 'Oct 16', spend: '$28', sales: '$90', clicks: '22', orders: '1', acos: '31.1%' },
          { day: 'Oct 17', spend: '$41', sales: '$140', clicks: '33', orders: '1', acos: '29.3%' },
          { day: 'Oct 18', spend: '$22', sales: '$85', clicks: '18', orders: '1', acos: '25.9%' },
          { day: 'Oct 19', spend: '$36', sales: '$122', clicks: '29', orders: '1', acos: '29.5%' },
          { day: 'Oct 20', spend: '$30', sales: '$96', clicks: '24', orders: '0', acos: '—' },
          { day: 'Oct 21', spend: '$24', sales: '$88', clicks: '19', orders: '1', acos: '27.3%' },
        ],
      },
      approveLabel: 'Approve Now',
    },
    {
      id: 'budget-1',
      title: 'Increase daily budget on “Kitchen Essentials - Exact”',
      badge: 'approval',
      badgeLabel: 'Requires Your Approval',
      meta: ['Kitchen-HighPriority', 'Sponsored Products', '3 Active Top SKUs'],
      from: '$80.00/d',
      to: '$90.00/d',
      delta: '+12.5%',
      reasonLabel: 'Constraint Detected',
      reason:
        'Campaign consistently exhausts budget at 2:30 PM with an 18.2% ACOS (below the 22% target). Projected +$1,420 incremental monthly sales.',
      confidence: '95%',
      risk: 'Medium Risk (>10%)',
      riskTone: 'medium',
      history: {
        window: 'Last 7 days',
        entity: 'Kitchen Essentials - Exact',
        rowsLabel: 'Each day',
        spend: '$560',
        sales: '$3,080',
        clicks: '511',
        orders: '77',
        acos: '18.2%',
        rows: [
          { day: 'Oct 15', spend: '$80', sales: '$440', clicks: '73', orders: '11', acos: '18.2%' },
          { day: 'Oct 16', spend: '$80', sales: '$440', clicks: '74', orders: '11', acos: '18.2%' },
          { day: 'Oct 17', spend: '$80', sales: '$440', clicks: '72', orders: '11', acos: '18.2%' },
          { day: 'Oct 18', spend: '$80', sales: '$440', clicks: '75', orders: '11', acos: '18.2%' },
          {
            day: 'Oct 19',
            spend: '$80',
            sales: '$440',
            clicks: '71',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM',
          },
          {
            day: 'Oct 20',
            spend: '$80',
            sales: '$440',
            clicks: '70',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM',
          },
          {
            day: 'Oct 21',
            spend: '$80',
            sales: '$440',
            clicks: '76',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM',
          },
        ],
      },
      approveLabel: 'Approve $90/day',
      dismissLabel: 'Reject Suggestion',
    },
    {
      id: 'neg-1',
      title: 'Add Negative Exact: “cheap plastic flasks bulk”',
      badge: 'approval',
      badgeLabel: 'Requires Your Approval',
      meta: ['Auto-Discovery CatchAll', 'Search Term Isolation', 'Catalog Integrity'],
      wasteAmount: '-$34.50',
      wasteDetail: '0 orders / 28 clicks',
      reasonLabel: 'Zero Conversion Wastage',
      reason:
        'Search term matches an auto campaign without matching product specs (stainless steel premium line vs plastic). Relevance score: 0.18.',
      confidence: '98%',
      risk: 'Low Risk',
      riskTone: 'low',
      history: {
        window: 'Last 14 days',
        entity: 'Search term',
        rowsLabel: 'Most recent 7 days',
        spend: '$34.50',
        sales: '$0',
        clicks: '28',
        orders: '0',
        acos: '—',
        rows: [
          { day: 'Oct 15', spend: '$4.10', sales: '$0', clicks: '3', orders: '0', acos: '—' },
          { day: 'Oct 16', spend: '$5.20', sales: '$0', clicks: '4', orders: '0', acos: '—' },
          { day: 'Oct 17', spend: '$0', sales: '$0', clicks: '0', orders: '0', acos: '—' },
          { day: 'Oct 18', spend: '$3.60', sales: '$0', clicks: '3', orders: '0', acos: '—' },
          { day: 'Oct 19', spend: '$2.50', sales: '$0', clicks: '2', orders: '0', acos: '—' },
          { day: 'Oct 20', spend: '$4.80', sales: '$0', clicks: '4', orders: '0', acos: '—' },
          { day: 'Oct 21', spend: '$1.20', sales: '$0', clicks: '1', orders: '0', acos: '—' },
        ],
      },
      approveLabel: 'Add Negative Exact',
      dismissLabel: 'Ignore Search Term',
    },
  ],
  guardrails: guardrails('+$100/day'),
};

const INDIA_DEMO: AgentDemo = {
  country: 'IN',
  account: {
    name: 'Anker Direct IN',
    marketplace: 'Amazon.in',
    activeCampaigns: 42,
    targetAcos: '22.0%',
    baselineAcos: '24.2%',
    dailyPace: '₹34,800/day',
    streamAgo: '2m ago',
  },
  user: USER,
  userGoal: USER_GOAL,
  agentReply: AGENT_REPLY,
  quickReplies: QUICK_REPLIES,
  promptChips: [
    {
      label: 'Show what changed today',
      prompt: 'Show what changed today across all 42 campaigns',
    },
    {
      label: 'Pause brand campaign changes',
      prompt: 'Pause brand defense campaign bid drops immediately',
    },
    {
      label: 'Why did ACOS increase yesterday?',
      prompt: 'Why did ACOS increase yesterday on the Amazon.in Hydro catalog?',
    },
    {
      label: 'Set max daily budget increase to ₹4,000',
      prompt: 'Set max daily budget increase to ₹4,000 without asking me',
    },
  ],
  rules: RULES,
  pulse: {
    spend: '₹10,48,000',
    sales: '₹43,68,000',
    ...PULSE_RATIOS,
  },
  opportunities: [
    {
      id: 'bid-1',
      title: 'Reduce bid on “stainless steel water bottle 1 litre”',
      badge: 'auto',
      badgeLabel: 'Eligible for Auto-Exec',
      meta: ['Kitchen-Hydro', 'Exact Match', 'ASIN: B0BHY1LTR9'],
      from: '₹104',
      to: '₹90',
      delta: '-13.5%',
      reasonLabel: 'Detection',
      reason:
        'ACOS currently at 28.4% with ₹34,300 spend and 11 orders (14-day window). Bid step down stabilizes conversion efficiency without dropping page rank.',
      confidence: '94%',
      risk: 'Low Risk',
      riskTone: 'low',
      explanation:
        'Amazon Marketing Stream hourly data indicates that between 1:00 PM and 5:00 PM IST, conversion drop rate is 22% higher on this specific keyword. Down-stepping the bid to ₹90 keeps top-of-search share at 68% while eliminating ₹6,500/week in overpaid clicks.',
      history: {
        window: 'Last 14 days',
        entity: 'Exact keyword',
        rowsLabel: 'Most recent 7 days',
        spend: '₹34,300',
        sales: '₹1,20,775',
        clicks: '330',
        orders: '11',
        acos: '28.4%',
        rows: [
          { day: 'Oct 15', spend: '₹2,660', sales: '₹9,820', clicks: '26', orders: '1', acos: '27.1%' },
          { day: 'Oct 16', spend: '₹2,330', sales: '₹7,490', clicks: '22', orders: '1', acos: '31.1%' },
          { day: 'Oct 17', spend: '₹3,410', sales: '₹11,640', clicks: '33', orders: '1', acos: '29.3%' },
          { day: 'Oct 18', spend: '₹1,830', sales: '₹7,070', clicks: '18', orders: '1', acos: '25.9%' },
          { day: 'Oct 19', spend: '₹3,000', sales: '₹10,170', clicks: '29', orders: '1', acos: '29.5%' },
          { day: 'Oct 20', spend: '₹2,500', sales: '₹8,000', clicks: '24', orders: '0', acos: '—' },
          { day: 'Oct 21', spend: '₹2,000', sales: '₹7,330', clicks: '19', orders: '1', acos: '27.3%' },
        ],
      },
      approveLabel: 'Approve Now',
    },
    {
      id: 'budget-1',
      title: 'Increase daily budget on “Kitchen Essentials - Exact”',
      badge: 'approval',
      badgeLabel: 'Requires Your Approval',
      meta: ['Kitchen-HighPriority', 'Sponsored Products', '3 Active Top SKUs'],
      from: '₹6,650/d',
      to: '₹7,480/d',
      delta: '+12.5%',
      reasonLabel: 'Constraint Detected',
      reason:
        'Campaign consistently exhausts budget at 2:30 PM IST with an 18.2% ACOS (below the 22% target). Projected +₹1,18,000 incremental monthly sales.',
      confidence: '95%',
      risk: 'Medium Risk (>10%)',
      riskTone: 'medium',
      history: {
        window: 'Last 7 days',
        entity: 'Kitchen Essentials - Exact',
        rowsLabel: 'Each day',
        spend: '₹46,550',
        sales: '₹2,55,780',
        clicks: '511',
        orders: '77',
        acos: '18.2%',
        rows: [
          { day: 'Oct 15', spend: '₹6,650', sales: '₹36,540', clicks: '73', orders: '11', acos: '18.2%' },
          { day: 'Oct 16', spend: '₹6,650', sales: '₹36,540', clicks: '74', orders: '11', acos: '18.2%' },
          { day: 'Oct 17', spend: '₹6,650', sales: '₹36,540', clicks: '72', orders: '11', acos: '18.2%' },
          { day: 'Oct 18', spend: '₹6,650', sales: '₹36,540', clicks: '75', orders: '11', acos: '18.2%' },
          {
            day: 'Oct 19',
            spend: '₹6,650',
            sales: '₹36,540',
            clicks: '71',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM IST',
          },
          {
            day: 'Oct 20',
            spend: '₹6,650',
            sales: '₹36,540',
            clicks: '70',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM IST',
          },
          {
            day: 'Oct 21',
            spend: '₹6,650',
            sales: '₹36,540',
            clicks: '76',
            orders: '11',
            acos: '18.2%',
            note: 'Out of budget 2:30 PM IST',
          },
        ],
      },
      approveLabel: 'Approve ₹7,480/day',
      dismissLabel: 'Reject Suggestion',
    },
    {
      id: 'neg-1',
      title: 'Add Negative Exact: “cheap plastic flasks bulk”',
      badge: 'approval',
      badgeLabel: 'Requires Your Approval',
      meta: ['Auto-Discovery CatchAll', 'Search Term Isolation', 'Catalog Integrity'],
      wasteAmount: '-₹2,870',
      wasteDetail: '0 orders / 28 clicks',
      reasonLabel: 'Zero Conversion Wastage',
      reason:
        'Search term matches an auto campaign without matching product specs (stainless steel premium line vs plastic). Relevance score: 0.18.',
      confidence: '98%',
      risk: 'Low Risk',
      riskTone: 'low',
      history: {
        window: 'Last 14 days',
        entity: 'Search term',
        rowsLabel: 'Most recent 7 days',
        spend: '₹2,870',
        sales: '₹0',
        clicks: '28',
        orders: '0',
        acos: '—',
        rows: [
          { day: 'Oct 15', spend: '₹340', sales: '₹0', clicks: '3', orders: '0', acos: '—' },
          { day: 'Oct 16', spend: '₹430', sales: '₹0', clicks: '4', orders: '0', acos: '—' },
          { day: 'Oct 17', spend: '₹0', sales: '₹0', clicks: '0', orders: '0', acos: '—' },
          { day: 'Oct 18', spend: '₹300', sales: '₹0', clicks: '3', orders: '0', acos: '—' },
          { day: 'Oct 19', spend: '₹210', sales: '₹0', clicks: '2', orders: '0', acos: '—' },
          { day: 'Oct 20', spend: '₹400', sales: '₹0', clicks: '4', orders: '0', acos: '—' },
          { day: 'Oct 21', spend: '₹100', sales: '₹0', clicks: '1', orders: '0', acos: '—' },
        ],
      },
      approveLabel: 'Add Negative Exact',
      dismissLabel: 'Ignore Search Term',
    },
  ],
  guardrails: guardrails('+₹8,300/day'),
};

const DEMOS: Record<AgentCountry, AgentDemo> = {
  US: US_DEMO,
  IN: INDIA_DEMO,
};

export function parseAgentCountry(value: string | null | undefined): AgentCountry {
  return value?.trim().toUpperCase() === 'IN' ? 'IN' : 'US';
}

export function getAgentDemo(country: AgentCountry): AgentDemo {
  return DEMOS[country];
}

export const SEED_TURNS: AgentTurn[] = [
  { id: 'greeting', role: 'seed', kind: 'greeting' },
  { id: 'user-goal', role: 'seed', kind: 'user-goal' },
  { id: 'rules', role: 'seed', kind: 'rules' },
  { id: 'discovery', role: 'seed', kind: 'discovery' },
];
